import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { queryOptions } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { listChangelogEntries, type ChangelogEntry } from "@/lib/backlink.functions";
import { SITE, formatDate } from "@/lib/site";

const changelogQuery = queryOptions({
  queryKey: ["changelog-entries"],
  queryFn: () => listChangelogEntries(),
});

export const Route = createFileRoute("/changelog/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(changelogQuery),
  head: () => {
    const url = `${SITE.url}/changelog`;
    return {
      meta: [
        { title: `Google Business Profile & Maps Changelog | ${SITE.name}` },
        {
          name: "description",
          content:
            "An editor-reviewed log of every confirmed change to Google Business Profile, Google Maps, and AI search surfaces. Permalinked, citable, kept up to date.",
        },
        { property: "og:title", content: `GBP & Google Maps Changelog | ${SITE.name}` },
        { property: "og:description", content: "Every confirmed change to GBP, Maps, and local AI search — permalinked." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Google Business Profile & Maps Changelog",
            url,
            isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
            description:
              "Editor-reviewed log of confirmed changes to Google Business Profile, Maps, and AI search surfaces.",
          }),
        },
      ],
    };
  },
  component: ChangelogPage,
});

function ChangelogPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <ChangelogBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

const SURFACE_LABEL: Record<string, string> = {
  gbp: "Business Profile",
  maps: "Google Maps",
  ai: "AI search",
  "search-console": "Search Console",
  merchant: "Merchant",
};

function ChangelogBody() {
  const { data } = useSuspenseQuery(changelogQuery);
  const entries = data as ChangelogEntry[];

  return (
    <div className="mx-auto max-w-4xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li>Changelog</li>
        </ol>
      </nav>
      <header className="mt-6 max-w-2xl">
        <p className="eyebrow">Live tracker</p>
        <h1 className="headline-display mt-3 text-5xl">GBP &amp; Google Maps changelog</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Every confirmed change to Google Business Profile, Google Maps, and local AI surfaces —
          dated, sourced, and permalinked so you can cite it.
        </p>
      </header>

      <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
        <CiteThis title="GBP & Google Maps Changelog" url={`${SITE.url}/changelog`} />
        <a
          href="/rss.xml"
          className="text-xs uppercase tracking-wider text-muted-foreground hover:text-accent"
        >
          RSS feed
        </a>
      </div>

      {entries.length === 0 ? (
        <p className="mt-16 text-center text-muted-foreground">No entries yet.</p>
      ) : (
        <ol className="mt-12 space-y-10 border-l-2 border-border pl-6">
          {entries.map((e) => {
            const target = resolveTarget(e.source_url, e.slug);
            const showSourceLine = e.source_url && target.kind !== "internal-article" && target.kind !== "external";
            return (
              <li key={e.id} className="relative">
                <span aria-hidden className="absolute -left-[31px] top-2 h-3 w-3 rounded-full bg-accent border-2 border-background" />
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex flex-wrap items-center gap-3">
                  <time dateTime={e.occurred_at}>{formatDate(e.occurred_at)}</time>
                  <span aria-hidden>·</span>
                  <span>{SURFACE_LABEL[e.surface] ?? e.surface}</span>
                  <span aria-hidden>·</span>
                  <span className="px-2 py-0.5 border border-border bg-paper-soft">{e.change_type}</span>
                </div>
                <h2 className="mt-2 text-xl font-semibold leading-tight">
                  <TitleLink target={target} title={e.title} />
                </h2>
                <p className="mt-2 text-base text-foreground/85 leading-relaxed">{e.summary}</p>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <Link to="/changelog/$slug" params={{ slug: e.slug }} className="underline text-foreground/80 hover:text-accent">
                    Permalink
                  </Link>
                  {showSourceLine && e.source_url && (
                    <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="underline text-muted-foreground hover:text-accent">
                      Source: {e.source_title ?? hostnameOf(e.source_url) ?? "official"}
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <NewsletterCTA />
    </div>
  );
}

type LinkTarget =
  | { kind: "internal-article"; slug: string }
  | { kind: "external"; href: string; host: string }
  | { kind: "permalink"; slug: string };

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function resolveTarget(sourceUrl: string | null, slug: string): LinkTarget {
  if (!sourceUrl) return { kind: "permalink", slug };
  try {
    const u = new URL(sourceUrl);
    const siteHost = new URL(SITE.url).hostname.replace(/^www\./, "");
    const host = u.hostname.replace(/^www\./, "");
    if (host === siteHost) {
      const m = u.pathname.match(/^\/article\/([^/]+)\/?$/);
      if (m) return { kind: "internal-article", slug: m[1] };
      return { kind: "permalink", slug };
    }
    return { kind: "external", href: sourceUrl, host };
  } catch {
    return { kind: "permalink", slug };
  }
}

function TitleLink({ target, title }: { target: LinkTarget; title: string }) {
  const cls = "hover:text-accent underline-offset-4 hover:underline";
  if (target.kind === "internal-article") {
    return (
      <Link to="/article/$slug" params={{ slug: target.slug }} className={cls}>
        {title}
      </Link>
    );
  }
  if (target.kind === "external") {
    return (
      <a
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${title} (opens on ${target.host})`}
        className={cls}
      >
        {title} <span aria-hidden className="text-muted-foreground text-sm">↗</span>
      </a>
    );
  }
  return (
    <Link to="/changelog/$slug" params={{ slug: target.slug }} className={cls}>
      {title}
    </Link>
  );
}

